import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["accessLevel", "actions", "objectId", "objectTypeId", "property", "value", "formMetadata", "enableConditionalPropertyOptions", "onFocus", "onBlur", "formControlProps", "getAdditionalPropertyValue", "getSavedValue", "getDraftValue"];
import { HIDDEN } from 'customer-data-objects/property/AccessLevel';
import { isFile } from 'customer-data-objects/property/PropertyIdentifier';
import { useMemo } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import useComponentMountMetric from '../../metrics/useComponentMountMetric';
import { usePropertyInputCanaryMetrics } from '../../metrics/usePropertyInputCanaryMetrics';
import { ActionsContext } from '../context/ActionsContext';
import { FocusProvider, useFocusContext } from '../context/FocusContext';
import { IdentifierProvider } from '../context/IdentifierContext';
import { PropertyContextProvider } from '../context/PropertyContext/PropertyContextProvider';
import { useConditionalPropertyOptions } from '../hooks/useConditionalPropertyOptions';
import { useGetAdditionalPropertyValue } from '../hooks/useGetAdditionalPropertyValue';
import { useHideFloatingEditIcon } from '../hooks/useHideFloatingEditIcon';
import { useObjectId } from '../hooks/useObjectId';
import { useObjectTypeId } from '../hooks/useObjectTypeId';
import { useProperty } from '../hooks/useProperty';
import { usePropertyAccessLevel } from '../hooks/usePropertyAccessLevel';
import { usePropertyInputComponent } from '../hooks/usePropertyInputComponent';
import { usePropertyValue } from '../hooks/usePropertyValue';
import { EDIT_INPUT_MODE } from '../types/PropertyInputV2Component';
import FormControl from './FormControl';
import InputWithFloatingFormControlFocus from './InputWithFloatingFormControlFocus';
import shouldShowAIComponentForStatus from '../../ai/shouldShowAIComponentForStatus';
import { usePropertyInputFeatures } from '../hooks/usePropertyInputFeatures';
import { usePropertyInputNormalization } from '../hooks/usePropertyInputNormalization';
import { ShowHighlySensitiveValueButton } from '../../dataSensitivity/ShowHighlySensitiveValueButton';
import { useIsUngatedForDisplayOptimizedFormControl } from '../hooks/useIsUngatedForDisplayOptimizedFormControl';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
const PropertyInputV2Wrapped = props => {
  const {
    autoFocus,
    disabled = false,
    enableAutosave = false,
    disableValidation = false,
    enableMultipleEmailInput = false,
    enablePopovers = true,
    enableCheckboxInput = false,
    enablePhoneNumberInput = true,
    enableConditionalPropertiesPanel = false,
    conditionalPropertyRules = undefined,
    FormControl: FormControlComponent,
    formControlProps = {},
    inputMode = EDIT_INPUT_MODE,
    inputProps = {},
    inputRef,
    onChange,
    onKeyUp,
    onSave = emptyFunction,
    onTracking,
    onValidationChange = emptyFunction,
    onOpenChange = emptyFunction,
    popoverProps,
    readOnly = false,
    use = 'default',
    validationSettings = {},
    showAutoSaveText
  } = props;
  const accessLevel = usePropertyAccessLevel();
  const property = useProperty();
  const propertyValue = usePropertyValue();
  const objectId = useObjectId();
  const objectTypeId = useObjectTypeId();
  const getAdditionalPropertyValue = useGetAdditionalPropertyValue();
  const {
    onFocus,
    onBlur
  } = useFocusContext();
  const showAutoSaveTextInternal = enableAutosave || showAutoSaveText;
  const {
    aiProcessingStatus,
    conditionalPropertiesPanel,
    isHidingHighlySensitiveValue,
    isPropertyReadOnly,
    leadStageValidationModal,
    onChangeMultiple,
    onChangeSingle,
    shouldValidate,
    validationState,
    wrappedOnBlur,
    wrappedOnFocus,
    wrappedOnOpenChange,
    wrappedOnValidationChange
  } = usePropertyInputFeatures({
    accessLevel,
    conditionalPropertyRules,
    disabled,
    disableValidation,
    enableAutosave,
    enableConditionalPropertiesPanel,
    getAdditionalPropertyValue,
    inputMode,
    objectId,
    objectTypeId,
    onBlur,
    onChange,
    onFocus,
    onOpenChange,
    onSave,
    onValidationChange,
    property,
    propertyValue,
    readOnly,
    showAutoSaveText: showAutoSaveTextInternal,
    use,
    validationSettings
  });
  const {
    invalid,
    message,
    additionalHelp
  } = validationState;
  const messageWithAdditionalHelp = additionalHelp !== undefined ? /*#__PURE__*/_jsxs(_Fragment, {
    children: [message, " ", additionalHelp]
  }) : message;
  const InputComponent = usePropertyInputComponent({
    isHidingHighlySensitiveValue,
    isPropertyReadOnly,
    enableMultipleEmailInput,
    enablePopovers,
    enableCheckboxInput,
    enablePhoneNumberInput,
    use
  });
  const shouldHideEditIcon = useHideFloatingEditIcon();
  const shouldUseHighlySensitiveFormControl = isHidingHighlySensitiveValue && accessLevel !== HIDDEN;
  const isUngatedForDisplayOptimizedFormControl = useIsUngatedForDisplayOptimizedFormControl();
  const highlySensitiveFormControlProps = useMemo(() => {
    if (shouldUseHighlySensitiveFormControl && !isUngatedForDisplayOptimizedFormControl) {
      return {
        overlayContent: /*#__PURE__*/_jsx(ShowHighlySensitiveValueButton, {
          isVisible: true
        }),
        hideEditIcon: true,
        isStatic: true
      };
    }
    return {};
  }, [isUngatedForDisplayOptimizedFormControl, shouldUseHighlySensitiveFormControl]);
  if (!InputComponent) {
    return null;
  }
  const isStatic = accessLevel === HIDDEN || isFile(property) || shouldShowAIComponentForStatus(aiProcessingStatus);
  const validationError = shouldValidate && invalid;
  const error = !!formControlProps.error || validationError ? true : formControlProps.error;
  const validationMessage = formControlProps.validationMessage || (shouldValidate ? messageWithAdditionalHelp : undefined);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(FormControl, Object.assign({
      Component: FormControlComponent
    }, formControlProps, {
      error: error,
      validationMessage: validationMessage,
      readOnly: !!formControlProps.readOnly || isPropertyReadOnly || disabled,
      hideEditIcon: isPropertyReadOnly || disabled || accessLevel === HIDDEN || shouldHideEditIcon,
      isStatic: isStatic,
      formControlUse: use
    }, highlySensitiveFormControlProps, {
      children: /*#__PURE__*/_jsx(InputWithFloatingFormControlFocus, Object.assign({
        InputComponent: InputComponent
      }, inputProps, {
        onOpenChange: wrappedOnOpenChange,
        autoFocus: autoFocus,
        disabled: disabled,
        inputMode: inputMode,
        inputRef: inputRef,
        onBlur: wrappedOnBlur
        // These change handlers are provided by usePropertyValidation
        // so it can intercept onChange events and trigger validation
        ,
        onChange: onChangeSingle,
        onChangeMultiple: onChangeMultiple,
        onFocus: wrappedOnFocus,
        onKeyUp: onKeyUp,
        onTracking: onTracking,
        onValidationChange: wrappedOnValidationChange,
        readOnly: isPropertyReadOnly,
        formControlUse: use,
        showAutoSaveText: showAutoSaveTextInternal,
        enablePopovers: enablePopovers,
        popoverProps: popoverProps,
        error: error,
        validationMessage: validationMessage,
        disableValidation: disableValidation
        // @ts-expect-error Intentionally omitted from the types since we do not want it to be
        // used. Instead `usePropertyValue()` can be used to get the value. We need to pass this
        // down to satisfy implicit behavior of floating form controls.
        ,
        value: propertyValue
      }))
    })), conditionalPropertiesPanel, leadStageValidationModal]
  });
};
const DefaultActions = {};
const PropertyInputV2 = props => {
  const {
      accessLevel: accessLevelOverride,
      actions = DefaultActions,
      objectId,
      objectTypeId,
      property,
      value,
      formMetadata,
      enableConditionalPropertyOptions,
      onFocus = emptyFunction,
      onBlur = emptyFunction,
      formControlProps,
      // @ts-expect-error TS confused by union, its OK if this pulls undefined
      getAdditionalPropertyValue,
      // @ts-expect-error TS confused by union, its OK if this pulls undefined
      getSavedValue,
      // @ts-expect-error TS confused by union, its OK if this pulls undefined
      getDraftValue
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  useComponentMountMetric('PropertyInputV2');
  usePropertyInputCanaryMetrics(props);
  const {
    normalizedObjectTypeId,
    normalizedProperty,
    normalizedGetAdditionalPropertyValue
  } = usePropertyInputNormalization({
    objectTypeId,
    property,
    getAdditionalPropertyValue,
    getDraftValue,
    getSavedValue
  });
  const {
    data: conditionalPropertyOptionsData
  } = useConditionalPropertyOptions({
    property: normalizedProperty,
    enableConditionalPropertyOptions,
    objectTypeId: normalizedObjectTypeId,
    objectId,
    getAdditionalPropertyValue: normalizedGetAdditionalPropertyValue,
    value
  });
  const formControlPropsWithConditionalPropertyOptions = useMemo(() => {
    if (!conditionalPropertyOptionsData) {
      return formControlProps;
    }

    // We want to show any validation errors from the conditional property options.
    // But, if the host app passed us an error, we defer to that instead.
    const {
      error,
      validationMessage
    } = conditionalPropertyOptionsData;
    if (formControlProps && formControlProps.error || !error) {
      return formControlProps;
    }
    return Object.assign({}, formControlProps, {
      error,
      validationMessage
    });
  }, [conditionalPropertyOptionsData, formControlProps]);
  const actionsContextValue = useMemo(() => ({
    actions
  }), [actions]);
  return /*#__PURE__*/_jsx(PropertyContextProvider, {
    accessLevelOverride: accessLevelOverride,
    objectTypeId: normalizedObjectTypeId,
    objectId: objectId,
    value: value,
    property: conditionalPropertyOptionsData ? conditionalPropertyOptionsData.property : normalizedProperty,
    autoManageHighlySensitiveValue: false,
    getAdditionalPropertyValue: normalizedGetAdditionalPropertyValue,
    children: /*#__PURE__*/_jsx(IdentifierProvider, {
      formMetadata: formMetadata,
      children: /*#__PURE__*/_jsx(ActionsContext.Provider, {
        value: actionsContextValue,
        children: /*#__PURE__*/_jsx(FocusProvider, {
          onFocus: onFocus,
          onBlur: onBlur,
          children: /*#__PURE__*/_jsx(PropertyInputV2Wrapped, Object.assign({}, rest, {
            formControlProps: formControlPropsWithConditionalPropertyOptions
          }))
        })
      })
    })
  });
};
export default PropertyInputV2;